import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, ShieldCheck } from "lucide-react";
import { CONTACT } from "@/content/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric" });
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${iso}T00:00:00`);
  return Math.round((target - today) / 86400000);
};

const pluralDays = (n) => {
  const abs = Math.abs(n);
  if (abs === 1) return "den";
  if (abs >= 2 && abs <= 4) return "dny";
  return "dní";
};

const RenewalInfo = ({ date }) => {
  if (!date) return null;
  const days = daysUntil(date);
  if (days < 0) {
    return (
      <p className="text-xs text-[#737373] mt-1" data-testid="pass-renewal-past">
        Výročí {formatDate(date)} — proběhlo, smlouvu je dobré zkontrolovat
      </p>
    );
  }
  if (days <= 60) {
    return (
      <p className="text-xs font-medium text-[#8A6D2F] mt-1" data-testid="pass-renewal-soon">
        Výročí {formatDate(date)} — za {days} {pluralDays(days)}
      </p>
    );
  }
  return (
    <p className="text-xs text-[#737373] mt-1" data-testid="pass-renewal-later">
      Výročí {formatDate(date)}
    </p>
  );
};

export default function ClientPass() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ok | notfound | error
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = "Pojišťovací pas | Daniel Pekař";
    const meta = document.head.querySelector('meta[name="robots"]');
    const prev = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "noindex, nofollow");
    return () => {
      document.title = "Daniel Pekař – finanční poradce Ústí nad Labem";
      if (meta && prev) meta.setAttribute("content", prev);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/client-pass/${token}`)
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) {
          setData(await res.json());
          setState("ok");
        } else if (res.status === 404) {
          setState("notfound");
        } else {
          setState("error");
        }
      })
      .catch(() => { if (!cancelled) setState("error"); });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <main className="min-h-screen bg-[#FAFAF8] py-16 md:py-24" data-testid="client-pass-page">
      <div className="max-w-3xl mx-auto px-6">
        <p className="font-display text-2xl text-[#252525]">Daniel Pekař</p>
        <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium mt-10">
          <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
          Pojišťovací pas
        </p>

        {state === "loading" && (
          <p className="text-base text-[#737373] mt-10" data-testid="pass-loading">Načítám váš přehled…</p>
        )}

        {(state === "notfound" || state === "error") && (
          <div className="mt-10 border border-[#F0F0F0] bg-white p-10" data-testid="pass-not-found">
            <h1 className="font-display text-3xl text-[#252525]">Pojišťovací pas nenalezen</h1>
            <p className="text-base text-[#4A4A4A] leading-relaxed mt-4">
              Odkaz je možná neplatný, nebo byl pas zrušen. Ozvěte se mi a připravím vám nový.
            </p>
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="pass-notfound-contact"
              className="mt-6 inline-flex items-center gap-2 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-6 py-3 text-xs uppercase tracking-widest font-medium"
            >
              Napsat Danielovi
            </a>
          </div>
        )}

        {state === "ok" && data && (
          <>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight leading-[1.15] text-[#252525] mt-6" data-testid="pass-title">
              Přehled smluv pro {data.client_name}
            </h1>
            <p className="text-base text-[#4A4A4A] leading-relaxed mt-4 max-w-xl">
              Vaše smlouvy na jednom místě. Když se něco změní, pas vám aktualizuji —
              a na výročí vás včas upozorním.
            </p>

            {data.note && (
              <p className="text-sm text-[#737373] leading-relaxed mt-6 border-l-2 border-[#B69A58] pl-4" data-testid="pass-note">
                {data.note}
              </p>
            )}

            <div className="mt-10 border border-[#F0F0F0] bg-white divide-y divide-[#F0F0F0]" data-testid="pass-contracts">
              {data.contracts.length === 0 && (
                <p className="p-8 text-sm text-[#737373]">Zatím tu nejsou žádné smlouvy — doplním je po naší schůzce.</p>
              )}
              {data.contracts.map((c, i) => (
                <div key={i} className="p-6 md:p-8 flex items-start justify-between gap-6" data-testid={`pass-contract-${i}`}>
                  <div>
                    <p className="font-display text-lg text-[#252525]">{c.label}</p>
                    {c.insurer && <p className="text-sm text-[#737373] mt-1">{c.insurer}</p>}
                    {c.note && <p className="text-sm text-[#4A4A4A] mt-2">{c.note}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <RenewalInfo date={c.renewal_date} />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 border border-[#F0F0F0] bg-white p-8" data-testid="pass-contact">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-[#B69A58]" strokeWidth={1.5} aria-hidden="true" />
                <h2 className="font-display text-xl text-[#252525]">Potřebujete něco změnit?</h2>
              </div>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mt-3">
                Ozvěte se mi — smlouvu upravíme, nebo doplníme do pasu další.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <a
                  href={CONTACT.phoneHref}
                  data-testid="pass-call-button"
                  className="inline-flex items-center justify-center gap-2 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-6 py-3 text-xs uppercase tracking-widest font-medium"
                >
                  <Phone className="h-4 w-4" strokeWidth={1.5} />
                  {CONTACT.phone}
                </a>
                <a
                  href={CONTACT.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pass-whatsapp-button"
                  className="inline-flex items-center justify-center gap-2 border border-[#E5E5E5] text-[#252525] hover:border-[#252525] transition-colors duration-300 px-6 py-3 text-xs uppercase tracking-widest font-medium"
                >
                  <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                  WhatsApp
                </a>
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="pass-email-button"
                  className="inline-flex items-center justify-center gap-2 border border-[#E5E5E5] text-[#252525] hover:border-[#252525] transition-colors duration-300 px-6 py-3 text-xs uppercase tracking-widest font-medium"
                >
                  <Mail className="h-4 w-4" strokeWidth={1.5} />
                  E-mail
                </a>
              </div>
            </div>

            <p className="text-xs text-[#737373] leading-relaxed mt-10 text-center">
              Tento přehled je osobní a neveřejný — neposílejte prosím odkaz dál.{" "}
              <Link to="/ochrana-osobnich-udaju" className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300">
                Ochrana osobních údajů
              </Link>
            </p>
          </>
        )}
      </div>
    </main>
  );
}
